import { useState, useMemo } from 'react';
import type { Vessel, VesselType } from '../types';
import { Ship, Plus, Search, Trash2, ChevronRight, Anchor } from 'lucide-react';

interface VesselSelectorProps {
  vessels: Vessel[];
  onSelect: (vessel: Vessel) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
}

const typeLabels: Record<VesselType, string> = {
  general_cargo: 'General Cargo',
  bulk_carrier: 'Bulk Carrier',
  container: 'Container',
  tanker: 'Tanker',
  chemical: 'Chemical',
  lpg: 'LPG',
  lng: 'LNG',
};

const typeColors: Record<VesselType, string> = {
  general_cargo: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  bulk_carrier: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  container: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  tanker: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  chemical: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  lpg: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  lng: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

const typeIcons: Record<VesselType, string> = {
  general_cargo: '📦',
  bulk_carrier: '🌾',
  container: '🚢',
  tanker: '🛢️',
  chemical: '⚗️',
  lpg: '🔥',
  lng: '❄️',
};

export default function VesselSelector({ vessels, onSelect, onAdd, onDelete }: VesselSelectorProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<VesselType | 'all'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return vessels.filter((v) => {
      const q = search.toLowerCase();
      const matchesSearch = v.name.toLowerCase().includes(q) || (v.imo || '').includes(q);
      const matchesType = filter === 'all' || v.type === filter;
      return matchesSearch && matchesType;
    });
  }, [vessels, search, filter]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-500" />
          <input
            type="text"
            placeholder="Search vessel or IMO..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-premium pl-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as VesselType | 'all')}
          className="input-premium sm:w-44"
        >
          <option value="all">All Types</option>
          {Object.entries(typeLabels).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Vessel Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((vessel) => (
          <div
            key={vessel.id}
            className="card-premium-hover group relative"
          >
            <button type="button"
              onClick={() => onSelect(vessel)}
              className="w-full p-4 text-left"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-navy-700/50 flex items-center justify-center text-xl shrink-0">
                  {typeIcons[vessel.type]}
                </div>
                <span className={`badge border ${typeColors[vessel.type]} text-[10px] sm:text-xs`}>
                  {typeLabels[vessel.type]}
                </span>
              </div>
              <h3 className="font-bold text-navy-100 text-sm sm:text-base mb-1 truncate">{vessel.name}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sm:text-xs text-navy-400 mb-3">
                {vessel.imo && <span>IMO: {vessel.imo}</span>}
                {vessel.lbp && <span>LBP: {vessel.lbp}m</span>}
                {vessel.dwt && <span>DWT: {vessel.dwt} MT</span>}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] text-navy-500">
                  <Anchor className="w-3 h-3" />
                  {vessel.hydrostatic?.length ? `${vessel.hydrostatic.length} hydro rows` : 'No hydro data'}
                </div>
                <ChevronRight className="w-4 h-4 text-navy-600 group-hover:text-ocean-400 transition-colors" />
              </div>
            </button>

            {/* Delete */}
            <button type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (deleteConfirm === vessel.id) {
                  onDelete(vessel.id);
                  setDeleteConfirm(null);
                } else {
                  setDeleteConfirm(vessel.id);
                  setTimeout(() => setDeleteConfirm(null), 3000);
                }
              }}
              className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-rose-500/20 text-navy-600 hover:text-rose-400 transition-colors opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            {deleteConfirm === vessel.id && (
              <div className="absolute top-8 right-2 bg-rose-500 text-white text-[10px] px-2 py-1 rounded font-bold animate-pulse">
                Click again to delete
              </div>
            )}
          </div>
        ))}

        {/* Add New */}
        <button type="button"
          onClick={onAdd}
          className="card-premium p-4 flex flex-col items-center justify-center gap-3 min-h-[120px] sm:min-h-[140px] border-dashed border-navy-600 hover:border-ocean-500/50 hover:bg-navy-800/60 transition-all group"
        >
          <div className="w-12 h-12 rounded-xl bg-navy-700/50 group-hover:bg-ocean-600/20 flex items-center justify-center transition-colors">
            <Plus className="w-6 h-6 text-navy-400 group-hover:text-ocean-400" />
          </div>
          <span className="text-sm font-medium text-navy-400 group-hover:text-navy-200">Add New Vessel</span>
        </button>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Ship className="w-12 h-12 text-navy-700 mx-auto mb-3" />
          <p className="text-navy-400 text-sm">No vessels found. Add your first vessel to begin.</p>
        </div>
      )}
    </div>
  );
}
